/**
 * Authenticated layout route
 *
 * Pathless layout route that wraps all protected pages.
 * Checks authentication status and redirects to login if needed.
 *
 * ## Auth outage grace mode
 *
 * When `authMode === "degraded"` (server unreachable but cached session
 * exists), the guard lets the user through instead of redirecting to
 * /login.  Individual data-fetching hooks will show their own error
 * states for API calls that fail during the outage.
 */
import type { ReactNode } from "react";
import { createFileRoute, Outlet, Navigate, useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/providers/auth-provider";
import { useUserSettings } from "@/hooks/use-user-settings";
import { isOnboardingGuardDisabled, shouldForceOnboarding } from "@/lib/onboarding-flags";

/**
 * Auth guard component
 *
 * Redirects to login if not authenticated.
 * Shows loading state while checking auth.
 * Allows access in degraded mode (cached session, server unreachable).
 */
function AuthGuard({ children }: { children: ReactNode }) {
  const { t } = useTranslation("common");
  const { isAuthenticated, isLoading, authMode } = useAuth();
  const location = useLocation();
  const { settings, isFetched: settingsFetched } = useUserSettings({
    enabled: isAuthenticated && authMode !== "degraded",
  });

  // Show loading while checking auth status (first load only)
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p>{t("loading")}</p>
      </div>
    );
  }

  // Redirect to login only when we positively know the user is
  // unauthenticated (401 / no session).  In "degraded" mode
  // `isAuthenticated` is true (from cached data) so this branch
  // is skipped — the user stays in the app.
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Skip onboarding checks in degraded mode — the settings endpoint
  // would fail anyway and we don't want to redirect during an outage.
  if (authMode !== "degraded") {
    const onboardingDisabled = isOnboardingGuardDisabled();
    const onboardingForced = shouldForceOnboarding();

    const needsOnboarding = settingsFetched && !!settings && settings.onboardingCompletedAt === null;

    if (!onboardingDisabled) {
      if (onboardingForced && location.pathname !== "/onboarding") {
        return <Navigate to="/onboarding" />;
      }
      if (needsOnboarding && location.pathname !== "/onboarding") {
        return <Navigate to="/onboarding" />;
      }
    }
  }

  return <>{children}</>;
}

/**
 * Authenticated layout component
 */
function AuthenticatedLayout() {
  return (
    <AuthGuard>
      <div className="authenticated-layout">
        <Outlet />
      </div>
    </AuthGuard>
  );
}

export const Route = createFileRoute("/_authenticated")({
  component: AuthenticatedLayout,
});
